import OfferBottomBar from 'components/jobOffer/bottomBar/OfferBottomBar';
import FaqsSection from 'components/jobOffer/faqs/FaqsSection';
import OfferFooter from 'components/jobOffer/footer/OfferFooter';
import HeroSection from 'components/jobOffer/hero/HeroSection';
import HowSection from 'components/jobOffer/how/HowSection';
import OfferSection from 'components/jobOffer/offer/OfferSection';
import SkillsSection from 'components/jobOffer/skills/SkillsSection';
import TeamSection from 'components/jobOffer/team/TeamSection';
import WhenSection from 'components/jobOffer/when/WhenSection';
import WhereSection from 'components/jobOffer/where/WhereSection';
import Page from 'components/layout/Page';
import { LoggedUserContext } from 'context/logged-user/loggeduser.context';
import { ManfrediteFieldsStatusContext } from 'context/manfredite-fields-status';
import { OfferApplyModalContext } from 'context/offer-apply-modal/offerapplymodal.context';
import { useI18n } from 'hooks/useI18n';
import { UIOfferSection } from 'interfaces/ui/ui-offer-section';
import { WebOffer } from 'model/model.web-offer';
import { WithTranslation, withTranslation } from 'next-i18next';
import { useRouter } from 'next/dist/client/router';
import dynamic from 'next/dynamic';
import { useContext, useEffect, useState } from 'react';
import OfferApplicationService from 'services/offer-application.service';
import ServiceEventTracking from 'services/service.event-tracking';
import { getOfferOGTitle } from 'services/ui/offer-og.service';
import { getCanonicalUrl } from 'services/ui/offer.service';
import { APP_URLS, getAppUrl } from 'utils/app-urls';
import RequirementsSection from './requirements/RequirementsSection';

const ShareModal = dynamic(() => import('components/shared/ShareModal'), {
  ssr: false
});

type JobOfferComponentProps = {
  offer: WebOffer;
  origin: string;
};

const JobOfferComponent: React.FC<JobOfferComponentProps & WithTranslation> = ({
  offer,
  origin
}) => {
  const { t } = useI18n('job-offer');
  const { user, refreshUserData } = useContext(LoggedUserContext);
  const router = useRouter();

  const { openOfferAplyModal } = useContext(OfferApplyModalContext);
  const { userCanApplyToOffers } = useContext(ManfrediteFieldsStatusContext);

  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);

  const onlyAvailableIn =
    offer.languages?.length === 1 ? offer.languages[0].code : null;

  const {
    migratedToMarkdown,
    footerData,
    heroSectionData,
    requirementsSectionData,
    howSectionData,
    whenSectionData,
    whereSectionData,
    teamSectionData,
    skillsSectionData,
    offerSectionData,
    faqsSectionData,
    bottombarData
  } = offer;

  const hasFaqs = offer.faqsSectionData.faqs?.length > 0;

  const offerSections: UIOfferSection[] = [
    { section: 'inicio', tooltip: t('titles.home') },
    { section: 'requisitos', tooltip: t('titles.requisites') },
    { section: 'como', tooltip: t('titles.how') },
    {
      section: 'horario',
      tooltip: t(
        offer.isFreelance ? 'titles.schedule_freelance' : 'titles.schedule'
      )
    },
    {
      section: 'donde',
      tooltip: t(offer.isFreelance ? 'titles.where_freelance' : 'titles.where')
    },
    {
      section: 'equipo',
      tooltip: t(offer.isFreelance ? 'titles.team_freelance' : 'titles.team')
    },
    { section: 'skills', tooltip: t('titles.skills') },
    { section: 'oferta', tooltip: t('titles.offer') }
  ];

  if (hasFaqs) {
    offerSections.push();
  }

  function canUserApplyToOffers(): boolean {
    return userCanApplyToOffers;
  }

  const handleApplyClick = async (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ): Promise<void> => {
    e.preventDefault();

    if (!canUserApplyToOffers()) {
      await OfferApplicationService.storeApplicationAttempt(offer.id);

      refreshUserData({
        attempt: {
          id: offer.id,
          position: {
            ES: offer.heroSectionData.jobTitle || '',
            EN: offer.heroSectionData.jobTitle || ''
          },
          slug: {
            ES: offer.slug,
            EN: offer.slug
          },
          companyName: offer.heroSectionData.companyName || ''
        }
      });

      router.push(getAppUrl(APP_URLS.portalV2Dashboard, router.locale));
    } else {
      openOfferAplyModal({
        offerId: offer.id,
        offer
      });
    }
  };

  function openShareModal(): void {
    setShareModalIsOpen(true);
  }

  function closeShareModal(): void {
    setShareModalIsOpen(false);
  }

  useEffect(() => {
    const offerURL = window.location.href;
    const urlParams = new URLSearchParams(window.location.search);

    ServiceEventTracking.track({
      type: 'Offers - Offer Viewed',
      properties: {
        offerCompany: bottombarData.companyName,
        offerName: bottombarData.internalCode,
        offerURL,
        pageLanguage: router.locale,
        urlParams: urlParams.toString() || undefined,
        utmCampaign: urlParams.get('utm_campaign') || undefined,
        utmContent: urlParams.get('utm_content') || undefined,
        utmId: urlParams.get('utm_id') || undefined,
        utmMedium: urlParams.get('utm_medium') || undefined,
        utmSource: urlParams.get('utm_source') || undefined,
        utmTerm: urlParams.get('utm_term') || undefined
      }
    });
  }, []);

  useEffect(() => {
    if (
      user?.offers?.attempt &&
      canUserApplyToOffers() &&
      router?.query?.action === 'apply'
    ) {
      openOfferAplyModal({
        offerId: user.offers.attempt.offerId,
        offer
      });
    }
  }, [user, canUserApplyToOffers]);

  return (
    <Page
      className="has-bottombar"
      sections={offerSections}
      offerFooter={
        <OfferFooter
          {...footerData}
          openShareModal={openShareModal}
          handleApplyClick={handleApplyClick}
          userApplied={user?.offers?.applications?.includes(offer.id)}
        />
      }
    >
      <HeroSection
        {...heroSectionData}
        onlyAvailableInLang={onlyAvailableIn}
        migratedToMarkdown={migratedToMarkdown}
      />

      {(requirementsSectionData.responsabilities?.length > 0 ||
        requirementsSectionData.requirementsIntro) && (
        <RequirementsSection
          {...requirementsSectionData}
          migratedToMarkdown={migratedToMarkdown}
        />
      )}

      {(howSectionData.howIntro || howSectionData.howItems.length > 0) && (
        <HowSection
          {...howSectionData}
          migratedToMarkdown={migratedToMarkdown}
        />
      )}

      {whenSectionData.whenIntro && (
        <WhenSection
          {...whenSectionData}
          migratedToMarkdown={migratedToMarkdown}
        />
      )}

      {whereSectionData.jobInfo && (
        <WhereSection
          {...whereSectionData}
          migratedToMarkdown={migratedToMarkdown}
        />
      )}

      {teamSectionData.teamInfo && (
        <TeamSection
          {...teamSectionData}
          migratedToMarkdown={migratedToMarkdown}
        />
      )}

      <SkillsSection
        {...skillsSectionData}
        migratedToMarkdown={migratedToMarkdown}
      />
      <OfferSection
        {...offerSectionData}
        migratedToMarkdown={migratedToMarkdown}
      />
      <FaqsSection
        {...faqsSectionData}
        migratedToMarkdown={migratedToMarkdown}
      />
      <OfferBottomBar
        {...bottombarData}
        sections={offerSections}
        openShareModal={openShareModal}
        handleApplyClick={handleApplyClick}
        userApplied={user?.offers?.applications?.includes(offer.id)}
        migratedToMarkdown={migratedToMarkdown}
      />

      {shareModalIsOpen && (
        <ShareModal
          isOpened={shareModalIsOpen}
          close={closeShareModal}
          url={getCanonicalUrl(origin, offer, router.locale)}
          emailSubject={getOfferOGTitle(offer, t, router.locale)}
          twitterMessage={t('share.text.twitter')}
          telegramMessage={t('share.text.telegram')}
          whatsappMessage={t('share.text.whatsapp')}
          emailMessage={t('share.text.email')}
        />
      )}
    </Page>
  );
};

export default withTranslation([
  'common',
  'job-offer',
  'apply-modal',
  'topbar',
  'footer'
])(JobOfferComponent);
